import { Box, Flex, Spinner } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { Heading } from "components/Heading";
import { QuoteCard } from "components/Pages/CreateRFQ/QuoteCard";
import QuoteTotal from "components/Pages/CreateRFQ/QuoteCard/QuoteTotal";
import { defaultOptions } from "components/Pages/CreateRFQ/QuoteCard/tableDefaultOptions";
import { FileUpload } from "components/RFQ/FileUpload";
import { FieldForm } from "components/RFQ/Input";
import { useRfxContext } from "newui/features/Rfx/context/RfxContext";
import { useField, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import tw, { styled } from "twin.macro";
import { reorderList } from "utils/reorderList";
import { removeProgressFromUploading } from "utils/rfxLargeFileUploadHelper";
import { Switch } from "components/Switch";
import { getFileNameFromUrl } from "utils/helpers";
import { FILE_UPLOAD_TYPES, fileUpload } from "newui/components/FileUpload";
import { FormattedInput } from "components/FormInputs/FormattedInput";

const QuoteContainer = styled.div(() => [tw`flex flex-col w-full`]);

const ButtonAddContent = ({ onClickAddTable }) => {
	return (
		<Box className="card-shadow z-40 rounded-md bg-white p-2">
			<Flex
				onClick={onClickAddTable}
				className="[transition-duration: 0.18s] relative cursor-pointer items-center gap-2 p-3 transition-all hover:rounded-md hover:bg-utility-blue-light-50"
			>
				<Icon
					icon="plus-circle"
					style={{ color: "#475467" }}
				/>
				<p
					className="font-roboto font-medium text-secondary-700"
					style={{ fontSize: "14px", marginTop: "1px" }}
					data-testid="add-table"
				>
					Add Table
				</p>
			</Flex>
		</Box>
	);
};
const QuestionerContainer = styled.div(({ isDisabled }) => [
	!isDisabled ? tw`opacity-100` : tw`opacity-50 pointer-events-none`,
]);

const Quote = ({
	subTitlePage,
	titlePage,
	setFieldValue,
	isTemplate,
	rfxOrRfxTemplateId,
}) => {
	const isLoading = false;

	const [, meta, helper] = useField("quote");
	const [, { value: typeValue }] = useField("quote_type");
	const { setUploadInProgresses } = useRfxContext();
	const { values } = useFormikContext();
	const [checked, setChecked] = useState(values.allow_flexible_quote === "1");
	const quotesTable = meta.value;
	const setQuotesTable = helper.setValue;

	const tableTypeOptions = [
		{ label: "Create my own quote format", value: "form", icon: "text-input" },
		{ label: "Upload Template", value: "file", icon: "template" },
	];

	const tableVariable = [
		"#",
		"Description of Scope",
		"Unit of Measurement",
		"Quantity",
		"Rate",
		"Cost",
	];

	const addTableHandler = () => {
		helper.setValue([
			...quotesTable,
			{
				name: "",
				unit_id: "",
				items: defaultOptions(),
				sort_order: quotesTable.length + 1,
				required: 1,
				isRemove: 0,
			},
		]);
	};

	const dragHandler = (result) => {
		let value = quotesTable;
		let valueSetter = (newValue) => (value = newValue);
		reorderList(value, result, valueSetter);
		const reordered = value.map((item, index) => ({
			...item,
			sort_order: index + 1,
		}));
		setQuotesTable(reordered);
	};

	useEffect(() => {
		if (quotesTable.length === 0) {
			addTableHandler();
		}
		//eslint-disable-next-line
	}, []);

	const handleFileUploadComplete = (uploadedFile) => {
		const fileId = isTemplate ? uploadedFile.file.id : uploadedFile?.id;
		const filePath = uploadedFile?.file;
		let serverFileName;

		if (isTemplate) {
			serverFileName = filePath.file_name;
		} else {
			serverFileName = filePath
				? getFileNameFromUrl(filePath)
				: "QuoteTemplate";
		}
		const downloadPath = isTemplate ? filePath.file_path : filePath;

		setFieldValue("quote_file", serverFileName);
		setFieldValue("quote_file_path", downloadPath || "");

		if (isTemplate && fileId) {
			setFieldValue("quote_file_id", fileId);
		} else if (!isTemplate) {
			setFieldValue("quote_file_id", null);
		} else {
			setFieldValue("quote_file_id", null);
			setFieldValue("quote_file", null);
			setFieldValue("quote_file_path", "");
		}

		setUploadInProgresses((prev) => removeProgressFromUploading(prev, true));
	};

	const onTemplateQuoteFileDelete = async (file) => {
		return fileUpload.util.deleteFileUpload(
			file,
			FILE_UPLOAD_TYPES.RFQ_QUOTE_FILE_TEMPLATE_TYPE
		);
	};

	return (
		<QuoteContainer>
			<div
				id="subheader-page"
				className="py-8"
			>
				<div
					id="subheader-page-title"
					className="text-xl font-semibold text-primary-900"
				>
					<Heading
						fontSize={"36px"}
						color="#003CE9"
					>
						{" "}
						{titlePage}
					</Heading>
				</div>
				<div
					id="subheader-page-subtitle"
					className="mt-1 whitespace-pre-line font-roboto text-sm leading-[1.5rem] text-tertiary-600"
				>
					{subTitlePage}
				</div>
			</div>
			<div>
				<div className="space-y-5 rounded-md bg-[#EEF5FFB2] p-8">
					<p className="text-base text-tertiary-600">
						Select an option below to get started:
					</p>
					<div className="flex flex-col items-start gap-x-12 gap-y-5 md:flex-row">
						<FieldForm
							name="quote_type"
							type="select"
							isContainImg={true}
							options={tableTypeOptions}
							placeholder="Select"
							className="!max-w-[340px]"
						/>
						<div>
							<Switch
								id="allow_flexible_quote"
								isChecked={checked}
								onChange={() => {
									const newValue = !checked;
									setChecked(newValue);
									setFieldValue("allow_flexible_quote", newValue ? "1" : "0");
								}}
								label="Allow Supplier File Upload"
								description="Allow suppliers to upload an alternative quote or supporting documentation as part of their submission."
								switchClassName="pt-1"
							/>
						</div>
					</div>
					{
						// @ts-ignore
						<FormattedInput
							label="Quote Instructions"
							name="quoteInstructions"
							characterLimit={3000}
							rfq
							className="w-full rounded-md border-solid border-gray-300 bg-white p-2"
							placeholder="(Optional) provide any additional instructions ans/or information that will help suppliers complete this quote section..."
							subTitle="Describe any additional information or instructions required to help suppliers complete this Quote section."
						/>
					}
				</div>
			</div>
			<div
				id="project-detail-content"
				className="relative h-full min-h-[60vh]"
			>
				{!isLoading ? (
					typeValue !== "file" ? (
						<>
							<QuestionerContainer>
								<DragDropContext
									onDragEnd={dragHandler}
									className="my-10"
								>
									<Droppable
										droppableId="questioner-container"
										direction="vertical"
									>
										{(provided) => (
											<div
												id="questioner-container"
												className="mb-8"
												{...provided.droppableProps}
												ref={provided.innerRef}
											>
												{quotesTable?.map((tableData, index) =>
													!tableData.isRemove ? (
														<Draggable
															key={`tableData-${index}`}
															draggableId={`tableData-${index}`}
															index={index}
														>
															{(provided) => (
																<div
																	ref={provided.innerRef}
																	{...provided.draggableProps}
																>
																	<QuoteCard
																		dragHandle={provided.dragHandleProps}
																		key={index}
																		initialDataItem={tableData}
																		tableVariable={tableVariable}
																		index={index}
																		quotesTable={quotesTable}
																		setQuotesTable={setQuotesTable}
																	/>
																</div>
															)}
														</Draggable>
													) : null
												)}

												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
								<Flex
									gap={4}
									className="my-6"
								>
									<QuoteTotal />
								</Flex>
								<Flex className="my-6">
									<ButtonAddContent onClickAddTable={() => addTableHandler()} />
								</Flex>
							</QuestionerContainer>
						</>
					) : (
						<div className="mt-12">
							<FileUpload
								className="!min-h-[400px]"
								label="Upload template"
								name="quote_file"
								types={["xlsx", "csv"]}
								maxFileSize={100}
								singleFile
								required={typeValue === "file"}
								inputBoxClassName="!min-h-[300px]"
								fileExtraData={{
									...(isTemplate
										? {
												model: FILE_UPLOAD_TYPES.RFQ_QUOTE_FILE_TEMPLATE_TYPE,
												rfxTemplateId: rfxOrRfxTemplateId,
											}
										: {
												rfq_id: rfxOrRfxTemplateId,
												model: "rfq_quote_file",
											}),
								}}
								handleFileUploadComplete={handleFileUploadComplete}
								isProcurerRFx
								stripHashFromFilename={!isTemplate}
								onDelete={onTemplateQuoteFileDelete}
							/>
						</div>
					)
				) : (
					<Spinner
						className="absolute left-1/2 top-[30vh] -translate-x-1/2"
						size="xl"
					/>
				)}
			</div>
		</QuoteContainer>
	);
};

export default Quote;
